import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { DateValueType, formatDate } from '@lib/utils.ts';
import {
  TReportActiveUsersWithNoAccounts,
  TReportBlacklistUsersParams,
} from '@src/types';
import { XCircle } from 'lucide-react';
import { getActiveUsersWithNoAccounts } from '@src/services/reports.ts';
import { deactivateUsers } from '@src/services/users.ts';
import { useNavigate } from 'react-router-dom';

export const ActiveUsersWithNoAccounts = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [data, setData] = useState<TReportActiveUsersWithNoAccounts[]>([]);
  const [processingTime, setProcessingTime] = useState<number>(0);

  const navigate = useNavigate();

  const fetchActiveUsersWithNoAccounts = async (
    params?: TReportBlacklistUsersParams
  ) => {
    const startTime = performance.now();

    const res = await getActiveUsersWithNoAccounts(params);

    const endTime = performance.now();
    const duration = endTime - startTime;

    setProcessingTime(duration);

    setData(res);
  };

  useEffect(() => {
    fetchActiveUsersWithNoAccounts().then();
  }, []);

  const handleGetData = async () => {
    await fetchActiveUsersWithNoAccounts({
      start_date: valueStart?.startDate
        ? format(valueStart.startDate, 'yyyy-MM-dd')
        : null,
      end_date: valueEnd?.startDate
        ? format(valueEnd.startDate, 'yyyy-MM-dd')
        : null,
    });
  };

  const handleDeactivateUsers = async () => {
    const ids = data.map((item) => item.id);

    await deactivateUsers(ids);
    await handleGetData();
  };

  const renderLevel = (level?: boolean, depth?: boolean) => {
    if (!level) {
      return <XCircle className={'size-4'} color={'red'} />;
    }
    if (depth) {
      return (
        <p className={'bg-lime py-[2px] px-1 rounded-[3px] w-fit'}>LVL2</p>
      );
    }

    return <p className={'bg-lime py-[2px] px-1 rounded-[3px] w-fit'}>LVL1</p>;
  };

  const columns = useMemo(
    (): TColumn<TReportActiveUsersWithNoAccounts>[] => [
      {
        title: 'Username',
        render: (item) => <p>{item?.username}</p>,
      },
      {
        title: 'Name',
        render: (item) => (
          <p>
            {item?.user.first_name} {item?.user.last_name}
          </p>
        ),
      },
      {
        title: 'Email',
        render: (item) => <p>{item?.user.email}</p>,
      },
      {
        title: 'Expiry',
        render: (item) => <p>{formatDate(item?.expire_date)}</p>,
      },
      {
        title: 'Accounts',
        render: (item) => (
          <p>
            Active: {item?.active ?? 0} | Closed: {item?.closed ?? 0}
          </p>
        ),
      },
      {
        title: 'CBOT',
        render: (item) => renderLevel(item?.cbot),
      },
      {
        title: 'CME',
        render: (item) => renderLevel(item?.cme),
      },
      {
        title: 'COMEX',
        render: (item) => renderLevel(item?.comex),
      },
      {
        title: 'NYMEX',
        render: (item) => renderLevel(item?.nymex),
      },
      {
        title: 'EUREX',
        render: (item) => renderLevel(item?.eurex),
      },
      {
        title: 'Mkt DEPTH',
        render: (item) => renderLevel(item?.market_depth),
      },
      {
        title: '',
        render: (item) => (
          <div>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => navigate(`/admin/users/${item?.id}`)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div>
      <div className={'flex flex-col gap-7.5'}>
        <div className={'text-xs flex flex-col gap-x-8 gap-y-3'}>
          <h1 className={'text-[23px]'}>{`Active Users with no accounts`}</h1>
          {data?.length ? (
            <p>
              <b>{data?.length} user(s)</b> have expiry dates in the future and
              no active accounts. To disable ALL of these users in RITHMIC and
              set the expiry date in MINT to{' '}
              {formatDate(new Date().toISOString())} click the button below.
              Alternatively EDIT each user and manually deactivate.
            </p>
          ) : null}
        </div>
        {data?.length ? (
          <div className={'flex gap-2 items-center'}>
            <p className={'text-xs'}>
              Processed in {Math.round(processingTime)}ms
            </p>
            <Button size={'sm'} onClick={handleDeactivateUsers}>
              DEACTIVATE ALL {data.length} USER(S)
            </Button>
          </div>
        ) : null}
      </div>
      <Card className={'mt-4.5'}>
        <div
          className={
            'flex justify-between items-start lg:flex-row flex-col mb-5 gap-6'
          }
        >
          <div className={'w-full flex gap-6 flex-wrap'}>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created From'}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created To'}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
          <div className={'flex gap-5'}>
            <Button size={'sm'} variant={'lime'} onClick={handleGetData}>
              Get
            </Button>
          </div>
        </div>
        <Divider />
        <RadixTable columns={columns} data={data} />
      </Card>
    </div>
  );
};
